import { useEffect, useState } from 'react';

import Skeleton from '@mui/material/Skeleton';

import Modal from '../../components/modal_dialog';
import { fetch as apiFetch } from '../../utils/api-wrapper';
import { formatMoney } from '../../utils/format-money';
import { formatDate } from './statement-display';

/**
 * Lets the user pick which transaction a statement line belongs to.
 *
 * Candidates are resolved live when the picker opens rather than read from the
 * reconciliation's ingest-time snapshot, so a transaction that synced after the
 * statement was imported can still be linked. The API answers from the same
 * live query, so nothing offered here can be rejected on submit.
 *
 * @param {object} props
 * @param {object} props.data - MobX data store containing the translator.
 * @param {string} props.reconciliationId - Reconciliation owning the line.
 * @param {object} props.item - The unmatched statement line being linked.
 * @param {boolean} props.saving - True while the link request is in flight.
 * @param {string|null} props.error - Message to show when the link failed.
 * @param {Function} props.onConfirm - Called with the chosen transaction id.
 * @param {Function} props.onCreate - Switches to creating a transaction instead.
 * @param {Function} props.onClose - Closes the picker. Dismissing after a
 *   transaction has been picked asks before the choice is thrown away.
 * @returns {JSX.Element} The transaction picker.
 */
const LinkTransactionModal = ({
  data,
  reconciliationId,
  item,
  saving = false,
  error = null,
  onConfirm,
  onCreate,
  onClose,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let active = true;
    apiFetch(
      `/c_card/statement-reconciliations/${reconciliationId}/items/${item.id}/candidates`,
      'GET',
      {},
      (response) => {
        if (!active) return;
        setTransactions(response?.data?.items || []);
      },
      () => {
        if (!active) return;
        setLoadFailed(true);
      }
    );
    return () => {
      active = false;
    };
  }, [reconciliationId, item.id]);

  return (
    <Modal
      title={data.t.read('cc_statements.statement_reconciliation.link_transaction')}
      subtitle={
        <>
          <strong>{item.description || '—'}</strong>
          {' · '}
          {formatDate(item.transaction_date || item.posted_date)}
          {' · '}
          {formatMoney(item.amount, item.currency || null)}
        </>
      }
      size='md'
      isDirty={selectedId !== null}
      cancelAction={onClose}
      cancelText={data.t.read('cancel')}
      okAction={() => {
        onConfirm(selectedId);
        // Keep the picker open so a failure can be retried without re-selecting.
        return false;
      }}
      okText={data.t.read('cc_statements.statement_reconciliation.link_confirm')}
      invalid={!selectedId || saving}
      secondaryActions={[{
        // Nothing to link to is the usual reason a line needs a new transaction,
        // so the escape hatch lives beside the list rather than in the table.
        text: data.t.read('cc_statements.statement_reconciliation.create_line_item'),
        action: () => onCreate?.(),
        disabled: saving,
        noAutoDisable: true,
      }]}
      noProcessingLoading
    >
      {loadFailed && (
        <p className='text-danger mb-0' role='alert'>
          {data.t.read('cc_statements.statement_reconciliation.candidates_load_failed')}
        </p>
      )}

      {!loadFailed && transactions === null && (
        <ul
          className='statement-link-options'
          aria-busy='true'
          aria-label={data.t.read('loading')}
          data-testid='link-candidates-skeleton'
        >
          {Array.from({ length: 3 }).map((_, option) => (
            <li key={`candidate-skeleton-${option}`}>
              <span className='statement-link-options__option'>
                <Skeleton variant='circular' width={16} height={16} sx={{ mt: 0.5 }} />
                <span className='flex-grow-1'>
                  <Skeleton variant='text' width='55%' />
                  <Skeleton variant='text' width='35%' />
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {!loadFailed && transactions?.length === 0 && (
        <p className='semi-faded mb-0'>
          {data.t.read('cc_statements.statement_reconciliation.no_linkable_transactions')}
        </p>
      )}

      {!loadFailed && transactions?.length > 0 && (
        <ul className='statement-link-options'>
          {transactions.map((transaction) => (
            <li key={transaction.id}>
              <label className='statement-link-options__option'>
                <input
                  type='radio'
                  name='statement-link-transaction'
                  value={transaction.id}
                  checked={selectedId === transaction.id}
                  onChange={() => setSelectedId(transaction.id)}
                />
                <span>
                  <strong>{transaction.description || '—'}</strong>
                  <span className='semi-faded d-block'>
                    {formatDate(transaction.authorized_date || transaction.posted_date)}
                    {' · '}
                    {formatMoney(transaction.amount, transaction.currency || null)}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {error && <div className='alert alert-danger mt-3' role='alert'>{error}</div>}
    </Modal>
  );
};

export default LinkTransactionModal;
